import {CONFIGURABLE_SETTINGS, SETTINGS} from "./settings/calendarSettings";
import {staticElements} from "../publishUtils";
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import {dateISOToDateUS, localizeDate} from "../../../../../utils/date";

function parseToCalendarDateFromObject(dateObject) {
    if (!dateObject) return new Date().toISOString().replace(/T.*$/, '');
    return dateObject.toISOString().replace(/T.*$/, '');
}

function today() {
    return parseToCalendarDateFromObject();
}

/**
 * Parse value into fullcalendar.io date format
 * @param ewiDate {string} takes a date in string format mm/dd/yyyy
 * @returns {string} returns the date the format that fullcalendar.io requires (yyyy-mm-dd).
 * If no date is specified it returns today.
 */
export function parseToCalendarDate(ewiDate) {
    if (!ewiDate) return today();
    const [month, day, year] = ewiDate.split('/');
    return `${year}-${month}-${day}`;
}

/**
 * Parse value into ewi date format
 * @param fullcalendarDate {string} takes a date in string format yyyy-mm-dd
 * @returns {string} returns the ewi date format (mm/dd/yyyy).
 * If no date is specified it returns today.
 */
export function parseToEWIDate(fullcalendarDate) {
    if (!fullcalendarDate) return today();
    const [year, month, day] = fullcalendarDate.split('-');
    return `${month}/${day}/${year}`;
}

/**
 * Get all year, month, day, hour and minute information of a date
 * @param fullcalendarDate {string} takes a date in string format yyyy-mm-dd
 * @returns {Object} returns object with year, month, day, hour and minute.
 */
export function getDateInformation(fullcalendarDate) {
    const [date, time] = fullcalendarDate.split('T');

    const [year, month, day] = date.split('-');

    const dateInfo = {year, month, day, ewiDate: parseToEWIDate(date)};

    if (time) {
        const [hour, minute] = time.split(':');
        dateInfo.hour = hour;
        dateInfo.minute = minute;
    }

    return dateInfo;
}

/**
 * Add one hour to the current time. Considers format & overflow.
 * @param hour {String}
 * @returns {String}
 */
export function addOneHour(hour) {
    const hourPlusOne = (+(hour) + 1) % 24;

    if (String(hourPlusOne).length === 1) return `0${hourPlusOne}`;

    return String(hourPlusOne);
}

export function getTimeText(instance) {
    if (instance.timeText.includes('AM') || instance.timeText.includes('PM')) {
        const [start, end] = instance.timeText.split(' - ');

        const to24h = (time) => {
            const [hours, minutes] = time.match(/\d+/g);
            const isPM = /PM/i.test(time);
            let h = parseInt(hours, 10);
            const m = minutes;

            if (isPM && h !== 12) h += 12;
            if (!isPM && h === 12) h = 0;

            return `${String(h).padStart(2, '0')}:${m}`;
        };

        return `${to24h(start)} - ${to24h(end)}`;
    }

    return instance.timeText;
}

/**
 * Calculates the difference in minutes between two times in a 12-hour format time range.
 * @param {string} timeRange - A string in the format "HH:MM - HH:MM" representing a time range where hours are in 12-hour format (0-12).
 * @returns {number} The difference in minutes between the two times. Returns a positive number
 *                   assuming the end time comes after the start time, potentially in the next
 *                   12-hour cycle (e.g., "12:00 - 01:00" is interpreted as 12:00 to 13:00).
 * @example
 * // Returns 75 (1 hour and 15 minutes)
 * timeDifference("09:30 - 10:45");
 * @example
 * // Returns 60 (interpreted as 12:00 to 13:00)
 * timeDifference("12:00 - 01:00");
 */
export function timeDifference(timeRange) {
    const [startTime, endTime] = timeRange.split(' - ');

    if (!endTime) return 60;

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    let startHour24 = startHour;
    if (startHour === 12) {
        startHour24 = 12;
    }

    let endHour24 = endHour;
    if (endHour < startHour || (endHour === startHour && endMinute < startMinute)) {
        endHour24 = endHour + 12;
    } else if (endHour === 12 && startHour !== 12) {
        endHour24 = 12;
    }

    const startTotalMinutes = startHour24 * 60 + startMinute;
    const endTotalMinutes = endHour24 * 60 + endMinute;

    return endTotalMinutes - startTotalMinutes;
}

/** @returns {string} */
export function getCalendarSettingsKey() {
    return `calendar-settings-${Laravel.activeUser}-${Laravel.activeProject}`
}

const StorageService = {
    getItem(key) {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } catch (error) {
            console.error('Error reading from localStorage', error);
            return null;
        }
    },

    setItem(key, value) {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error('Error saving to localStorage', error);
        }
    },
};

/**
 * @returns {CalendarSettings}
 */
export function loadSettings(defaultSettings) {
    const settingsKey = getCalendarSettingsKey();

    const savedSettings = StorageService.getItem(settingsKey);

    if (savedSettings) {
        return {
            ...SETTINGS,
            ...defaultSettings,
            ...savedSettings
        };
    }

    return { ...SETTINGS, ...defaultSettings};
}

/**
 * @returns {boolean}
 */
function getIs24TimeFormat() {
    if (Laravel.timepickerMode === 1) return true;
    if (Laravel.timepickerMode === 2) return false;

    throw Error('Laravel.timepickerMode is in an invalid state!');
}

/**
 * Creates calendar options object with the provided settings
 * @param {Array} events - The events for the calendar including background events
 * @param {Object} handlers - Event handlers for the calendar
 * @param {Function} handlers.handleDrop - Handler for drop events
 @param {Function} handlers.handleEventChange - Handler for event change
 @param {Function} handlers.handleEventAllow - Handler for event allow
 * @param {Object} businessHours - Array of business hours according to fullcalendar.io
 * @param {boolean} disableSettings - Whether to disable settings button
 * @returns {Object} Full calendar options
 */
export function createCalendarOptions(events, handlers, businessHours, disableSettings = false) {
    const loadedSettings = loadSettings();

    const options = {
        ...loadedSettings,
        plugins: [
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
            listPlugin
        ],
        events: events,
        drop: handlers.handleDrop,
        eventChange: handlers.handleEventChange,
        eventAllow: handlers.handleEventAllow,
        headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: disableSettings
                ? 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
                : 'settingsButton dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        }
    };

    if (Laravel.timepickerMode !== 0) {
        const is24TimeFormat = getIs24TimeFormat()

        options.slotLabelFormat = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: is24TimeFormat
        };
        options.eventTimeFormat = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: is24TimeFormat
        };
    }

    if (loadedSettings.showSwitchOffTimes) {
        options.businessHours = businessHours;
    }

    if (!disableSettings) {
        options.customButtons = {
            settingsButton: {
                text: Lang.langChoice('Setting|Settings', 2),
                click() {
                    EaseEventBus.fire('calendar-clickedSettings');
                }
            }
        };
    }

    return options;
}

/**
 * Creates a background event to indicate disabled time periods
 * @param {string} disabledUntil - ISO date string representing when the disabled period ends
 * @returns {Object} Background event object
 */
export function createDisabledPeriodEvent(disabledUntil) {
    return {
        start: '1000-01-01',
        end: disabledUntil,
        overlap: false,
        display: 'background',
        color: '#ff9f89'
    };
}

/**
 * Combines regular events with any background events
 * @param {Array} events - Regular calendar events
 * @param {string|null} disabledUntil - ISO date string for disabled period end, or null if no disabled period
 * @param {boolean} dragging - User is currently dragging
 * @returns {Array} Combined events including background events if applicable
 */
export function combineEventsWithBackground(events, disabledUntil, dragging) {
    const combinedEvents = [...events];

    const todayDate = new Date(today());

    if (disabledUntil) {
        const disabledUntilDate = new Date(disabledUntil);
        disabledUntil = (disabledUntilDate < todayDate)
            ? parseToCalendarDateFromObject(todayDate)
            : parseToCalendarDateFromObject(disabledUntilDate);

        combinedEvents.push(createDisabledPeriodEvent(disabledUntil));
        return combinedEvents
    }

    if (dragging) {
        combinedEvents.push(createDisabledPeriodEvent(parseToCalendarDateFromObject(todayDate)));
        return combinedEvents
    }

    return combinedEvents;
}

/**
 * @param newSettings {CalendarSettings}
 */
export function saveSettings(newSettings) {
    const settingsKey = getCalendarSettingsKey();

    const filteredSettings = {};

    Object.keys(CONFIGURABLE_SETTINGS).forEach(key => {
        if (key in newSettings) {
            filteredSettings[key] = newSettings[key];
        } else {
            filteredSettings[key] = CONFIGURABLE_SETTINGS[key];
        }
    });

    StorageService.setItem(settingsKey, filteredSettings);
}

/**
 * @param type {String}
 * @returns {{legacy: boolean, name: string, type: string, eicon: string}}
 */
export function getStaticElementByType(type) {
    return staticElements.find(element => element.type === type);
}

/**
 * @param element {CalendarPublishElement}
 * @returns {String}
 */
export function getEventTitle(element) {
    const data = element?.data;

    if (data && data.name) {
        return data.name;
    }

    if (data && data.type) {
        const foundElement = getStaticElementByType(data.type);
        if (foundElement) {
            return foundElement.name;
        }
    }

    return "Unknown";
}

/**
 * Converts the switch-off-times JSON format to FullCalendar's business hours format
 * @param {Object} switchOffData - The JSON data from the backend
 * @returns {Array} Business hours array for FullCalendar
 */
export function parseBusinessHours(switchOffData) {
    // FullCalendar uses 0-6 for days (0 = Sunday, 6 = Saturday)
    const dayMapping = {
        'sunday': 0,
        'monday': 1,
        'tuesday': 2,
        'wednesday': 3,
        'thursday': 4,
        'friday': 5,
        'saturday': 6
    };

    const businessHours = [];
    const allConfig = switchOffData.all;
    const days = switchOffData.days;

    if (allConfig.active) {
        const startTime = `${allConfig.from.hh}:${allConfig.from.mm}`;
        const endTime = `${allConfig.to.hh}:${allConfig.to.mm}`;

        businessHours.push({
            daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
            startTime: startTime,
            endTime: endTime
        });
    } else {
        for (const [day, config] of Object.entries(days)) {
            if (config.active) {
                const startTime = `${config.from.hh}:${config.from.mm}`;
                const endTime = `${config.to.hh}:${config.to.mm}`;

                businessHours.push({
                    daysOfWeek: [dayMapping[day]],
                    startTime: startTime,
                    endTime: endTime
                });
            }
        }
    }

    return businessHours;
}

/**
 * Formats a date with optional time
 * @param {string} date - The date string to format
 * @param {string} time - Optional time string
 * @returns {string} Formatted date string
 */
function formatEventDate(date, time) {
    const calendarDate = parseToCalendarDate(date);
    return time ? `${calendarDate}T${time}` : calendarDate;
}

/**
 * Creates an event object from an element
 * @param {CalendarPublishElement} element - The element to convert to an event
 * @param {CalendarPublishLayer} layer - The layer containing the element
 * @returns {Object} Formatted event object
 */
function createEventObject(element, layer) {
    const start = formatEventDate(element.data.validFrom, element.data.validTimeFrom);
    const end = formatEventDate(element.data.validTo, element.data.validTimeTo);

    const elementData = {
        ...element,
        layer: {width: layer.width, height: layer.height}
    };

    return {
        id: element.id,
        title: getEventTitle(element),
        start,
        end,
        elementData
    };
}

/**
 * Returns current events from a layer's elements
 * @param {CalendarPublishLayer} layer - The active layer containing elements
 * @param {Boolean} [showDeleted] - Show deleted events (disabled per default)
 * @returns {Array} List of formatted event objects
 */
export function getCurrentEvents(layer, showDeleted) {
    let elements = layer.elements;

    if (!elements) return [];

    if (!showDeleted) {
        elements = elements.filter(element => !element.deletedAt);
    }

    return elements.map(element => createEventObject(element, layer));
}

function doRangesOverlap(from1, to1, from2, to2) {
    const toMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
    };

    const start1 = toMinutes(from1);
    const end1 = toMinutes(to1);
    const start2 = toMinutes(from2);
    const end2 = toMinutes(to2);

    return start1 < end2 && start2 < end1;
}

/**
 * Validates the time change and returns a reason if it fails
 * @param elements
 * @param elementId
 * @param newFrom
 * @param newTo
 * @returns {{valid: boolean}|{valid: boolean, reason: string}}
 */
export function validateTimeChange(elements, elementId, newFrom, newTo) {
    const currentElement = elements.find(el => el.id === elementId);
    if (!currentElement) return { valid: false, reason: "Element not found" };

    const proposedFrom = newFrom || currentElement.validTimeFrom;
    const proposedTo = newTo || currentElement.validTimeTo;

    if (!isValidTimeFormat(proposedFrom) || !isValidTimeFormat(proposedTo)) {
        return { valid: false, reason: "Invalid time format" };
    }

    if (proposedFrom >= proposedTo) {
        return { valid: false, reason: "Start time must be before end time" };
    }

    for (const element of elements) {
        if (element.id === elementId) continue;

        if (doRangesOverlap(
            proposedFrom, proposedTo,
            element.validTimeFrom, element.validTimeTo
        )) {
            return {
                valid: false,
                reason: `Overlaps with element ${element.name} (${element.validTimeFrom}-${element.validTimeTo})`
            };
        }
    }

    return { valid: true };
}

function isValidTimeFormat(timeStr) {
    const regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return regex.test(timeStr);
}

/**
 * Filters elements that are active on the specified day
 * @param {Array} elements - All elements in the layer
 * @param {string} targetDate - Date in format MM/DD/YYYY
 * @returns {Array} - Elements active on the specified day
 */
export function filterElementsByDay(elements, targetDate) {
    const targetDateObj = parseDate(targetDate);

    return elements.filter(element => {
        const startDateObj = parseDate(element.startDate);
        const endDateObj = parseDate(element.endDate);

        return targetDateObj >= startDateObj && targetDateObj <= endDateObj;
    });
}

/**
 * Parse date string into Date object
 * @param {string} dateStr - Date in format MM/DD/YYYY
 * @returns {Date} - Date object
 */
function parseDate(dateStr) {
    const [month, day, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
}


/**
 * Formats two date objects into a range string using FullCalendar's formatting utilities
 * For use with Vue 2 FullCalendar component
 *
 * @param {Date|undefined} startDate - The start date/time
 * @param {Date|undefined} endDate - The end date/time
 * @param {Object} calendarRef - Reference to the FullCalendar Vue component
 * @param {boolean} [isAllDay=false] - Whether the event is an all-day event
 * @returns {string} Formatted date/time range string or empty string if dates are invalid
 */
export function formatDateTimeRange(startDate, endDate, calendarRef, isAllDay = false) {
    const calendarApi = calendarRef.getApi();

    if (isAllDay && startDate && !endDate) {
        return calendarApi.formatDate(startDate, { weekday: 'long' });
    }

    if (!startDate || !endDate || !calendarRef ||
        !(startDate instanceof Date) || !(endDate instanceof Date)
        || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return '';
    }

    const sameDay = startDate.getDate() === endDate.getDate() &&
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getFullYear() === endDate.getFullYear();


    // Case 1: All-day single-day event
    if (isAllDay && sameDay) {
        return calendarApi.formatDate(startDate, { weekday: 'long' });
    }

    // Case 2: Same day with time range
    if (sameDay && !isAllDay) {
        const dayFormat = calendarApi.formatDate(startDate, { weekday: 'short' });
        const startTimeFormat = calendarApi.formatDate(startDate, { hour: '2-digit', minute: '2-digit' });
        const endTimeFormat = calendarApi.formatDate(endDate, { hour: '2-digit', minute: '2-digit' });

        return `${dayFormat} ${startTimeFormat} - ${endTimeFormat}`;
    // Case 3: Multiple days
    } else {
        const sameMonth = startDate.getMonth() === endDate.getMonth();

        const startMonthFormat = calendarApi.formatDate(startDate, { month: 'long' });
        const endMonthFormat = calendarApi.formatDate(endDate, { month: 'long' });
        const startDayFormat = calendarApi.formatDate(startDate, { day: '2-digit' });
        const endDayFormat = calendarApi.formatDate(endDate, { day: '2-digit' });

        if (sameMonth) {
            return `${startDayFormat} ${startMonthFormat} - ${endDayFormat}`;
        } else {
            return `${startDayFormat} ${startMonthFormat} - ${endDayFormat} ${endMonthFormat}`;
        }
    }
}

/**
 * Filters elements that end before a specified date (defaults to today).
 *
 * @param {CalendarPublishElement[]} elements - Array of elements with date properties
 * @param {string} [dateString] - Optional date string in format "MM/DD/YYYY"
 * @returns {CalendarPublishElement[]} - Filtered elements that end before the specified date
 */
export function getElementsBeforeDate(elements, dateString) {
    const referenceDate = dateString
        ? new Date(dateString)
        : new Date();

    referenceDate.setHours(0, 0, 0, 0);

    return elements.filter(el => {
        const elementDate = new Date(el.data.validTo);
        elementDate.setHours(0, 0, 0, 0);
        return elementDate < referenceDate;
    });
}

/**
 * Returns a date string (MM/DD/YYYY) based on a relative time keyword.
 *
 * @param {'immediately'|'week'|'month'} range
 * @returns {string}
 */
export function getRelativeDateString(range) {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // normalize to midnight

    let resultDate;
    switch (range) {
        case 'immediately':
            resultDate = today;
            break;
        case 'week':
            resultDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
            break;
        case 'month':
            resultDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
            break;
        default:
            throw new Error(`Unsupported range: ${range}`);
    }

    const mm = String(resultDate.getMonth() + 1).padStart(2, '0');
    const dd = String(resultDate.getDate()).padStart(2, '0');
    const yyyy = resultDate.getFullYear();

    return `${mm}/${dd}/${yyyy}`;
}

/**
 * @param expiryTimeName {'immediately'|'week'|'month'}
 * @returns {String}
 */
export function getExpiryMessage(expiryTimeName) {
    const now = new Date();
    let expiryDate;

    switch (expiryTimeName) {
        case 'immediately':
            expiryDate = now;
            break;
        case 'week':
            expiryDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
            break;
        case 'month':
            expiryDate = new Date(now);
            expiryDate.setMonth(expiryDate.getMonth() - 1);
            break;
        default:
            expiryDate = now;
    }

    const dateISO = expiryDate.toISOString().split('T')[0];
    const dateUs = dateISOToDateUS(dateISO);
    const formattedDate = localizeDate(dateUs);

    return Lang.langGet("When you publish your events before :date will be deleted", {date: formattedDate});
}

export function timeStringToMinutes(timeStr = '00:15:00') {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return hours * 60 + minutes + Math.floor(seconds / 60);
}

export function subtractOneDay(dateStr) {
    const [month, day, year] = dateStr.split('/').map(Number);

    // Month is still 0-based in JS Date
    const date = new Date(year, month - 1, day);

    date.setDate(date.getDate() - 1);

    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const y = date.getFullYear();

    return `${m}/${d}/${y}`;
}
